import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import clsx from 'clsx';

import { Modal } from '@/common/Modal';
import { Search } from '@/components/common';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { AssetType } from '@/enums';
import { TABS } from '@/enums/OrganizationAssetTabsEnum';
import { useGetOrganizationAssets } from '@/services';
import { useFilterStore } from '@/store';
import { Asset } from '@/types';

import { AssetItem } from './AssetItem';

interface OrganizationAssetsModalProps {
  isOpenOrganizationAssetsModal: boolean;
  setIsOpenOrganizationAssetsModal: (isOpen: boolean) => void;
  handleSelectAsset: (asset: Asset) => void;
  setIsUploading: Dispatch<SetStateAction<boolean>>;
  organizationId?: number | null;
}

export const OrganizationAssetsModal = ({
  isOpenOrganizationAssetsModal,
  setIsOpenOrganizationAssetsModal,
  handleSelectAsset,
  setIsUploading,
  organizationId
}: OrganizationAssetsModalProps) => {
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [t] = useTranslation('common');
  const { currentUserData } = useContext(AuthContext);
  const { organizationFilter } = useFilterStore();
  const { ref: scrollRef, inView } = useInView();

  const [currentTab, setCurrentTab] = useState<string>(TABS.ALL_ASSET);
  const [currentAssetType, setCurrentAssetType] = useState<string>('');
  const [search, setSearch] = useState<string>();
  const [isSubmiting, setIsSubmitting] = useState<boolean>(false);

  const getOrganizationValue = () => {
    if (organizationId) {
      return organizationId;
    }
    if (organizationFilter && organizationFilter.value) {
      return organizationFilter.value as number;
    }

    if (currentUserData?.organizations.length === 1) {
      return currentUserData?.organizations[0].id as number;
    }

    return undefined;
  };

  const {
    data: organizationAssetsData,
    fetchNextPage: organizationAssetsFetchNextPage,
    hasNextPage: organizationAssetsHasNextPage
  } = useGetOrganizationAssets(
    PAGE_SIZE,
    true,
    getOrganizationValue(),
    search,
    currentAssetType ? [currentAssetType] : undefined,
    'CreatedAt',
    false
  );

  useEffect(() => {
    if (inView && organizationAssetsHasNextPage) {
      organizationAssetsFetchNextPage();
    }
  }, [inView, organizationAssetsHasNextPage]);

  const handleCurrentTab = (tab: TABS) => {
    setCurrentTab(tab);
    switch (tab) {
      case TABS.ALL_ASSET:
        setCurrentAssetType('');
        break;
      case TABS.PHOTO:
        setCurrentAssetType(String(AssetType.IMAGE));
        break;
      case TABS.VIDEO:
        setCurrentAssetType(String(AssetType.VIDEO));
        break;
      case TABS.MUSIC:
        setCurrentAssetType(String(AssetType.AUDIO));
        break;
      case TABS.TEXT_OVERLAY:
        setCurrentAssetType(String(AssetType.TEXT_OVERLAY));
        break;
      default:
        setCurrentAssetType('');
    }
  };

  const handleAddSelectedAssets = async () => {
    if (selectedAssets.length > 0) {
      setIsSubmitting(true);
      setIsUploading(true);
      await Promise.all(selectedAssets.map((asset) => handleSelectAsset(asset)));
      setSelectedAssets([]);
      setIsOpenOrganizationAssetsModal(false);
      setIsUploading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      modalClasses='md:w-[60vw] w-[90vw]'
      containerClasses='h-full'
      height='h-[90vh]'
      hasButton={true}
      hasCancelButton
      buttonText={t('add')}
      buttonClasses='flex justify-end'
      buttonType={ButtonVariant.PRIMARY}
      isButtonDisabled={selectedAssets.length === 0 || isSubmiting}
      label={t('organizationAssets')}
      isOpen={isOpenOrganizationAssetsModal}
      onClose={() => setIsOpenOrganizationAssetsModal(false)}
      handleSubmit={handleAddSelectedAssets}>
      <div className='flex h-full flex-col'>
        <div className='mb-6 flex w-full justify-between'>
          <div className='flex'>
            {Object.values(TABS).map((tab) => (
              <div
                key={tab}
                className={clsx(
                  'flex cursor-pointer whitespace-nowrap rounded px-5 py-2.5',
                  currentTab === tab && 'bg-secondary-pale-blue-gray font-bold'
                )}
                onClick={() => {
                  handleCurrentTab(tab);
                }}>
                {t(tab)}
              </div>
            ))}
          </div>
          <div>
            <Search callback={setSearch} />
          </div>
        </div>
        <div className='no-scrollbar grid flex-1 grid-cols-3 gap-2 overflow-y-auto md:grid-cols-5 md:gap-4'>
          {organizationAssetsData?.pages
            .flatMap((page) => page.data?.items)
            .map((asset: Asset) => {
              return (
                <div key={asset.id} className='group relative'>
                  <div
                    onClick={() => {
                      setSelectedAssets((prev) => {
                        if (prev.some((a) => a.id === asset.id)) {
                          return prev.filter((a) => a.id !== asset.id);
                        } else {
                          return [...prev, asset];
                        }
                      });
                    }}>
                    <AssetItem
                      setSelectedAsset={undefined}
                      canBeSelected
                      canBeDeleted={false}
                      asset={asset}
                      key={asset.id}
                      assetType={asset.assetType}
                      selectedVideoUrl={''}
                      multiselect={true}
                      selectedAssets={selectedAssets}
                    />
                  </div>
                </div>
              );
            })}
          <div ref={scrollRef} className='size-1'></div>
        </div>
      </div>
    </Modal>
  );
};
